import { FC, useEffect, useRef } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout } from '@eneco/flows/components';
import { Box, Button, Heading, Stack } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { Placeholder, RenderingProvider, usePlaceholderContent, useLayoutData } from '@sitecore/common';
import type { B2BLeadFlowThankYouStepRendering } from '@sitecore/types/B2BLeadFlowThankYouStep';
import { ComponentRendering } from '@sitecore/types/lib';

import { useFlowTracking } from '../hooks/useFlowTracking';

export const ThankYouStep: FC = () => {
  const { B2BLeadFlowThankYouStep } = usePlaceholderContent<{
    B2BLeadFlowThankYouStep: Omit<B2BLeadFlowThankYouStepRendering, 'placeholders'>;
  }>();
  const { trackFunnelCompleted } = useTracking();
  const { trackPurchase } = useFlowTracking();

  const completionTracked = useRef(false);
  const rendering = B2BLeadFlowThankYouStep as unknown as ComponentRendering;
  const textualData = B2BLeadFlowThankYouStep?.fields;

  const {
    route: { fields = {} },
  } = useLayoutData();

  const handleClose = () => {
    // @ts-ignore TODO: remove this ignore once "fields" type is fixed "libs/sitecore/common/src/types.ts"
    const completionUrl = fields?.flowCompletionUrl?.value?.href;
    if (completionUrl) {
      window.location.href = completionUrl;
    }
  };

  useEffect(() => {
    if (completionTracked.current) {
      return;
    }
    completionTracked.current = true;
    const flowName = fields?.flowName?.value?.toString() || 'b2bLeadFlow';
    trackPurchase();
    trackFunnelCompleted({
      funnel: flowName,
    });
  }, [fields, trackFunnelCompleted, trackPurchase]);

  return (
    <RenderingProvider rendering={rendering}>
      <Layout.Content variant="A">
        <Header>
          <Heading as="h1" size="L">
            {textualData?.content?.title?.value}
          </Heading>
          {textualData?.content?.text?.value && (
            <Header.Text>
              <RichText html={textualData.content.text.value} />
            </Header.Text>
          )}
        </Header>

        <Box>
          <Placeholder name="jss-b2b-lead-flow-step-explainer" />
        </Box>

        {textualData?.contactInformation && (
          <Box paddingBottom={6}>
            <Stack direction="column" gap={4}>
              {textualData.contactInformation.contactInformationTitle?.value && (
                <Heading as="h2" size="M">
                  {textualData.contactInformation.contactInformationTitle.value}
                </Heading>
              )}
              {textualData.contactInformation.contactInformationContent?.value && (
                <RichText html={textualData.contactInformation.contactInformationContent.value} />
              )}
            </Stack>
          </Box>
        )}

        {textualData?.content?.nextStepText?.value && (
          <Button onClick={handleClose} action="primary">
            {textualData.content.nextStepText.value}
          </Button>
        )}
      </Layout.Content>
    </RenderingProvider>
  );
};
